import i18n from '@/i18n'
import globalMethods from "@/globalMethods";
import {URLS, RESPONSES, domain} from "@/constants";
import {notify} from "@/plugins/notification";


export default {
    namespaced: true,
    state: {
        isAuthorized: localStorage.getItem('isAuthorized') === 'true',
        isMobileVersion: true,
        isPWA: false,
        appInitialized: false,
    },
    mutations: {
        setIsAuthorized(state, newValue) {
            state.isAuthorized = newValue;
            if (newValue) {
                localStorage.setItem('isAuthorized', 'true');
            } else {
                localStorage.removeItem('isAuthorized');
            }
        },
        setIsMobileVersion(state, newValue) {
            state.isMobileVersion = newValue;
        },
        setIsPWA(state, newValue) {
            state.isPWA = newValue;
        },
        setAppInitialized(state) {
            state.appInitialized = true;
        }
    },
    actions: {
        changeIsMobileVersion({commit}, newValue) {
            commit("setIsMobileVersion", newValue);
        },
        setIsPWA({commit}, newValue) {
            commit("setIsPWA", newValue);
        },
        async getCSRFToken({rootState}) {
            const {personalData} = rootState;
            const options = {
                method: "GET",
                credentials: "include",
                headers: {
                    role: personalData.role,
                }
            };
            const successfulHandler = (data) => {
                if (data.csrftoken) {
                    globalMethods._nativeTokens["csrftoken"] = data.csrftoken;
                    document.cookie = `csrftoken=${data.csrftoken}; path=/`;
                }
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: URLS.AUTH.GET_CSRF_TOKEN, options: options, successfulHandler: successfulHandler});
        },
        async checkToken(_, token) {
            const url = URLS.AUTH.CHECK_TEMPORARY_TOKEN_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: "include",
                body: JSON.stringify({
                    temporary_token: token
                })
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options});
        },
        async checkTokenToConfirmAccount(_, token) {
            const url = `${URLS.AUTH.CONFIRMATION_ACCOUNT_URL}?temporary_token=${token}`;
            const options = {
                method: "GET",
                credentials: "include",
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options});
        },
        async checkTokenToResetEmail(_, temporary_token) {
            const url = `${URLS.AUTH.CHANGE_EMAIL_FINAL_URL}?temporary_token=${temporary_token}`;
            const options = {
                method: "GET",
                credentials: "include",
            };
            const successfulHandler = (data) => {
                return {successfully: true, currentEmail: data["current_email"], nextEmail: data["next_email"]};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },

        async checkAuthentication({commit}) {
            const url = URLS.AUTH.CHECK_AUTHENTICATION_URL;
            const options = {
                method: "GET",
                credentials: "include",
            };
            const successfulHandler = () => {
                commit("personalData/setRole", globalMethods.getCookie("role"), {root: true});
                commit("company/setCompanyId", globalMethods.getCookie("companyId"), {root: true});
                commit("setIsAuthorized", true);
                return {successfully: true};
            };
            const errorHandler = () => {
                commit("setIsAuthorized", false);
                return {successfully: false};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler, errorHandler: errorHandler});
        },
        async confirmAccount({commit}, data) {
            const {temporary_token, password} = data;
            const url = URLS.AUTH.CONFIRMATION_ACCOUNT_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: "include",
                body: JSON.stringify({
                    password: password,
                    temporary_token: temporary_token,
                })
            };
            const successfulHandler = () => {
                commit("setIsAuthorized", true);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },

        async resetPhoneStart({rootState}) {
            const {personalData, company} = rootState;
            const url = URLS.AUTH.CHANGE_PHONE_START_URL;
            const options = {
                method: "POST",
                headers: {
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
            };
            const successfulHandler = () => {
                notify(i18n.global.t("The request to change your phone number has been sent. Please check your email"));
                return {successfully: true};
            };
            const errorHandler = (data) => {
                if (data.detail === RESPONSES.TOO_MANY_REQUESTS_IN_SHORT_TIME) {
                    notify(i18n.global.t("Too many requests. Please try again in 10 minutes"), "red");
                    return {successfully: false};
                }
                return globalMethods.defaultErrorHandler();
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler, errorHandler: errorHandler});
        },
        async resetPhoneFinal({commit}, data) {
            const { temporary_token, phone} = data;
            const url = URLS.AUTH.CHANGE_PHONE_FINAL_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: "include",
                body: JSON.stringify({
                    phone: phone,
                    temporary_token: temporary_token,
                })
            };
            const successfulHandler = () => {
                commit("setIsAuthorized", true);
                return {successfully: true};
            };
            const errorHandler = (data) => {
                return {successfully: false, detail: data.detail};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler, errorHandler: errorHandler});
        },

        async resetPasswordStart({dispatch}, email) {
            await dispatch('getCSRFToken');
            const url = URLS.AUTH.RESET_PASSWORD_START_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: "include",
                body: JSON.stringify({ email: email }),
            };
            const successfulHandler = () => {
                notify(i18n.global.t("Password reset request has been sent. Please check your email"), "black");
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async resetPasswordFinal({commit}, data) {
            const { temporary_token, password} = data;
            const url = URLS.AUTH.RESET_PASSWORD_FINAL_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: "include",
                body: JSON.stringify({
                    password: password,
                    temporary_token: temporary_token,
                })
            };
            const successfulHandler = () => {
                commit("setIsAuthorized", true);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },

        async resetEmailStart({rootState}) {
            const {personalData, company} = rootState;
            const url = URLS.AUTH.CHANGE_EMAIL_START_URL;
            const options = {
                method: "POST",
                headers: {
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
            };
            const successfulHandler = () => {
                notify(i18n.global.t("Email change request has been sent. Please check your email"));
                return {successfully: true};
            };
            const errorHandler = (data) => {
                if (data.detail === RESPONSES.TOO_MANY_REQUESTS_IN_SHORT_TIME) {
                    notify(i18n.global.t("Too many requests. Please try again in 10 minutes"), "red");
                    return {successfully: false};
                }
                return globalMethods.defaultErrorHandler();
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler, errorHandler: errorHandler});
        },
        async resetEmailContinue(_, data) {
            const {temporary_token, email} = data;
            const url = URLS.AUTH.CHANGE_EMAIL_CONTINUE_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                body: JSON.stringify({
                    temporary_token: temporary_token,
                    email: email,
                }),
                credentials: "include",
            };
            const successfulHandler = () => {
                notify(i18n.global.t("We’ve sent a confirmation email to the address provided. Please check your email"), "black");
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async resetEmailFinal(_, temporary_token) {
            const url = URLS.AUTH.CHANGE_EMAIL_FINAL_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                body: JSON.stringify({
                    temporary_token: temporary_token,
                }),
                credentials: 'include',
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options});
        },
        async cancelResetEmailProcess(_, temporary_token) {
            const url = URLS.AUTH.CHANGE_EMAIL_FINAL_URL;
            const options = {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                body: JSON.stringify({
                    temporary_token: temporary_token,
                }),
                credentials: "include",
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options});
        },
        async deleteAccount({commit}) {
            const url = URLS.AUTH.DELETE_ACCOUNT_URL;
            const options = {
                method: "DELETE",
                headers: {
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: "include",
            };
            const successfulHandler = () => {
                commit("setIsAuthorized", false);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },

        async signUp({dispatch}, data) {
            await dispatch('getCSRFToken');
            const { username, name, phone, captcha } = data;
            const url = URLS.AUTH.SIGN_UP_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: "include",
                body: JSON.stringify({
                    email: username,
                    name: name,
                    phone: phone,
                    captcha: captcha,
                    utm: {
                        source: globalMethods.getCookie("_utm_source"),
                        medium: globalMethods.getCookie("_utm_medium"),
                        campaign: globalMethods.getCookie("_utm_campaign"),
                        term: globalMethods.getCookie("_utm_term"),
                        content: globalMethods.getCookie("_utm_content"),
                    },
                }),
            };
            const successfulHandler = () => {
                globalMethods.deleteCookie("_utm_source");
                globalMethods.deleteCookie("_utm_medium");
                globalMethods.deleteCookie("_utm_campaign");
                globalMethods.deleteCookie("_utm_term");
                globalMethods.deleteCookie("_utm_content");
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async signIn({commit, dispatch}, credentials) {
            await dispatch('getCSRFToken');
            const {username, password} = credentials;
            const url = URLS.AUTH.SIGN_IN_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: 'include',
                body: JSON.stringify({
                    "username": username,
                    "password": password,
                })
            };
            const successfulHandler = (data) => {
                notify(i18n.global.t("Sign in successful!"), "black");
                // Wipe stale store state that could leak from a previous account
                // (e.g. old role/companyId cookies, cached staff permissions, etc.)
                commit("personalData/setRole", null, {root: true});
                commit("personalData/setPersonalData", {}, {root: true});
                commit("personalData/setPersonalStaffData", {}, {root: true});
                commit("company/setCompanyData", {}, {root: true});
                commit("cashRegister/setCashRegisterData", {}, {root: true});
                commit("cashRegister/setCashRegisters", [], {root: true});
                commit("transaction/setTransactions", [], {root: true});
                commit("good/setGoods", [], {root: true});
                commit("staff/setCashiers", [], {root: true});
                commit("salesSearch/setSales", {data: [], hasNext: false, totalCount: 0, append: false}, {root: true});
                commit("salesSearch/setLastFilters", null, {root: true});
                commit("invoice/setInvoices", {invoices: [], outstandingTotal: 0, totalCount: 0, hasMore: false, filters: null}, {root: true});

                commit("company/setCompanyId", data["company-id"], {root: true});
                commit("setIsAuthorized", true);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async signInWithAppleNative({commit, dispatch}, {idToken}) {
            await dispatch('getCSRFToken');
            const url = `${domain}/api/auth/apple-id/`;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                },
                credentials: "include",
                body: JSON.stringify({id_token: idToken}),
            };
            const successfulHandler = () => {
                commit("setIsAuthorized", true);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async signOut({commit, dispatch}) {
            try {
                await fetch(URLS.AUTH.SIGN_OUT_URL, {
                    method: "POST",
                    headers: {"X-CSRFToken": globalMethods.getCookie("csrftoken")},
                    credentials: "include",
                });
            } catch (e) {
                // Ignore errors — signing out locally regardless
            }

            dispatch('salesSSE/disconnect', null, {root: true});

            commit("setIsAuthorized", false);
            commit("personalData/setRole", null, {root: true});
            commit("personalData/setPersonalData", {}, {root: true});
            commit("company/setCompanyData", {}, {root: true});
            commit("cashRegister/setCashRegisterData", {}, {root: true});
            commit("cashRegister/setCashRegisters", [], {root: true});
            commit("transaction/setTransactions", [], {root: true});
            commit("staff/setCashiers", [], {root: true});
            commit("good/setGoods", [], {root: true});
            commit("good/setFilterWarehouseId", "all", {root: true});
            commit("good/setFilterShowHidden", false, {root: true});
            commit("goodCategory/setCategories", [], {root: true});
            commit("goodCategory/setSelectedCategoryId", null, {root: true});
            commit("goodCategory/setSelectedKindFilter", null, {root: true});
            commit("salesSearch/setSales", {data: [], hasNext: false, totalCount: 0, append: false}, {root: true});
            commit("salesSearch/setLastFilters", null, {root: true});
            commit("invoice/setInvoices", {invoices: [], outstandingTotal: 0, totalCount: 0, hasMore: false, filters: null}, {root: true});

            // Otherwise the next user reuses the same id, KassaView.activated()
            // sees no change, and skips loadData.
            localStorage.removeItem("currentCashRegisterId");

            return {successfully: true};
        },
    },
};